import { makeAutoObservable } from "mobx";

class BasketStore {
  constructor() {
    this._items = [];
    this._selected = {};

    makeAutoObservable(this);

    this.loadBasket();
    this.loadSelection();

    this.initSelectionDefaults();
    this.normalizeSelectionConflicts();
    this.cleanupSelectionKeys();
  }

  toNum(v) {
    if (v == null) return 0;
    if (typeof v === "number") return Number.isFinite(v) ? v : 0;

    const n = Number(
      String(v)
        .replace(/[^\d.,-]/g, "")
        .replace(",", "."),
    );
    return Number.isFinite(n) ? n : 0;
  }

  getItemStockQty(item) {
    const raw = item?.stockQuantity ?? item?.quantity ?? 0;
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  }

  isOOS(item) {
    const qty = this.getItemStockQty(item);
    const count = Number(item?.count || 1);
    return qty <= 0 || qty < count;
  }

  getItemSellerId(item) {
    const n = Number(item?.sellerId);
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  get selectedSellerId() {
    for (const i of this.selectedItems) {
      const sid = this.getItemSellerId(i);
      if (sid) return sid;
    }
    return null;
  }

  get hasSelectedDifferentSellers() {
    const ids = new Set();
    for (const i of this.selectedItems) {
      const sid = this.getItemSellerId(i);
      if (sid) ids.add(sid);
      if (ids.size > 1) return true;
    }
    return false;
  }

  get stockItems() {
    return this._items.filter((i) => !this.isOOS(i));
  }

  get oosItems() {
    return this._items.filter((i) => this.isOOS(i));
  }

  isSelected(uniqueKey) {
    return !!this._selected[uniqueKey];
  }

  get selectedItems() {
    return this._items.filter((i) => this._selected[i.uniqueKey]);
  }

  get hasSelectedPreorders() {
    return this._items.some(
      (i) => this._selected[i.uniqueKey] && this.isOOS(i),
    );
  }

  get hasSelectedStockItems() {
    return this._items.some(
      (i) => this._selected[i.uniqueKey] && !this.isOOS(i),
    );
  }

  canSelectKey(uniqueKey) {
    const item = this._items.find((i) => i.uniqueKey === uniqueKey);
    if (!item) return false;

    const itemIsOOS = this.isOOS(item);

    if (itemIsOOS && this.hasSelectedStockItems) return false;
    if (!itemIsOOS && this.hasSelectedPreorders) return false;

    const activeSellerId = this.selectedSellerId;
    const itemSellerId = this.getItemSellerId(item);

    if (activeSellerId && itemSellerId && activeSellerId !== itemSellerId) {
      return false;
    }

    return true;
  }

  toggleSelect(uniqueKey) {
    const item = this._items.find((i) => i.uniqueKey === uniqueKey);
    if (!item) return false;

    const next = !this._selected[uniqueKey];

    if (next && !this.canSelectKey(uniqueKey)) return false;

    this._selected[uniqueKey] = next;

    this.normalizeSelectionConflicts();
    this.saveSelection();
    return true;
  }

  setSelected(uniqueKey, value) {
    const item = this._items.find((i) => i.uniqueKey === uniqueKey);
    if (!item) return false;

    const next = !!value;

    if (next && !this.canSelectKey(uniqueKey)) return false;

    this._selected[uniqueKey] = next;

    this.normalizeSelectionConflicts();
    this.saveSelection();
    return true;
  }

  initSelectionDefaults() {
    for (const item of this._items) {
      if (this._selected[item.uniqueKey] == null) {
        this._selected[item.uniqueKey] = !this.isOOS(item);
      }
    }
    this.normalizeSelectionConflicts();
    this.saveSelection();
  }

  normalizeSelectionConflicts() {
    const hasStock = this.hasSelectedStockItems;
    const hasOOS = this.hasSelectedPreorders;

    if (hasStock && hasOOS) {
      for (const item of this._items) {
        if (this.isOOS(item)) {
          this._selected[item.uniqueKey] = false;
        }
      }
    }

    const allowedSellerId = this.selectedSellerId;

    if (allowedSellerId) {
      for (const item of this._items) {
        if (!this._selected[item.uniqueKey]) continue;

        const sid = this.getItemSellerId(item);
        if (sid && sid !== allowedSellerId) {
          this._selected[item.uniqueKey] = false;
        }
      }
    }
  }

  cleanupSelectionKeys() {
    const keys = new Set(this._items.map((i) => i.uniqueKey));
    for (const k of Object.keys(this._selected)) {
      if (!keys.has(k)) delete this._selected[k];
    }
    this.saveSelection();
  }

  clearSelection() {
    this._selected = {};
    this.saveSelection();
  }

  get allSelectedStockItems() {
    const list = this.stockItems;
    return list.length > 0 && list.every((i) => this.isSelected(i.uniqueKey));
  }

  get allSelectedOOSItems() {
    const list = this.oosItems;
    return list.length > 0 && list.every((i) => this.isSelected(i.uniqueKey));
  }

  clearStockSelection() {
    for (const item of this.stockItems) {
      this._selected[item.uniqueKey] = false;
    }
  }

  clearOOSSelection() {
    for (const item of this.oosItems) {
      this._selected[item.uniqueKey] = false;
    }
  }

  selectAllStock() {
    this.clearOOSSelection();
    for (const item of this.stockItems) {
      this._selected[item.uniqueKey] = true;
    }
    this.normalizeSelectionConflicts();
  }

  selectAllOOS() {
    this.clearStockSelection();
    for (const item of this.oosItems) {
      this._selected[item.uniqueKey] = true;
    }
    this.normalizeSelectionConflicts();
  }

  toggleSelectAllStock() {
    if (this.allSelectedStockItems) {
      this.clearStockSelection();
    } else {
      this.selectAllStock();
    }
    this.normalizeSelectionConflicts();
    this.saveSelection();
  }

  toggleSelectAllOOS() {
    if (this.allSelectedOOSItems) {
      this.clearOOSSelection();
    } else {
      this.selectAllOOS();
    }
    this.normalizeSelectionConflicts();
    this.saveSelection();
  }

  removeSelectedItems() {
    const keys = new Set(
      this._items.map((i) => i.uniqueKey).filter((k) => this._selected[k]),
    );

    this._items = this._items.filter((i) => !keys.has(i.uniqueKey));
    for (const k of keys) delete this._selected[k];

    this.saveBasket();
    this.saveSelection();
  }

  addItem(item) {
    const newSellerId = this.getItemSellerId(item);
    const existingSellerIds = new Set(
      this._items.map((i) => this.getItemSellerId(i)),
    );

    if (existingSellerIds.size > 0) {
      if (!existingSellerIds.has(newSellerId)) {
        const ok = window.confirm(
          "В корзине уже есть товары другого продавца. Очистить корзину и добавить этот товар?",
        );

        if (!ok) {
          return;
        }

        this.clearItems();
      }
    }

    const uniqueKey = JSON.stringify({
      id: item.id,
      options: item.selectedOptions,
    });

    const existingItem = this._items.find((i) => i.uniqueKey === uniqueKey);

    if (existingItem) {
      existingItem.count += 1;

      if (this.isOOS(existingItem)) {
        this._selected[uniqueKey] = false;
      }

      this.normalizeSelectionConflicts();
      this.saveBasket();
      this.saveSelection();
      return;
    }

    const newItem = {
      ...item,
      selectedOptions: item.selectedOptions || {},
      count: 1,
      uniqueKey,
    };

    this._items.push(newItem);

    if (this._selected[uniqueKey] == null) {
      const activeSellerId = this.selectedSellerId;
      const itemSellerId = this.getItemSellerId(newItem);

      const sellerOk =
        !activeSellerId || !itemSellerId || activeSellerId === itemSellerId;

      const defaultSelected =
        sellerOk && !this.isOOS(newItem) && !this.hasSelectedPreorders;

      this._selected[uniqueKey] = defaultSelected;
    }

    this.normalizeSelectionConflicts();
    this.saveBasket();
    this.saveSelection();
  }

  removeItem(uniqueKey) {
    this._items = this._items.filter((item) => item.uniqueKey !== uniqueKey);
    delete this._selected[uniqueKey];

    this.saveBasket();
    this.saveSelection();
  }

  updateItemCount(uniqueKey, count) {
    const item = this._items.find((i) => i.uniqueKey === uniqueKey);
    if (!item) return;

    item.count = count;

    if (this.isOOS(item)) {
      this._selected[uniqueKey] = false;
    }

    this.normalizeSelectionConflicts();
    this.saveBasket();
    this.saveSelection();
  }

  updateSelectedOption(itemUniqueKey, optionName, updatedOption) {
    const item = this._items.find((i) => i.uniqueKey === itemUniqueKey);
    if (!item) return;

    if (!item.selectedOptions) item.selectedOptions = {};
    item.selectedOptions[optionName] = updatedOption;

    this.normalizeSelectionConflicts();

    this.saveBasket();
    this.saveSelection();
  }

  getItemCount(uniqueKey) {
    const item = this._items.find((i) => i.uniqueKey === uniqueKey);
    return item ? item.count : 0;
  }

  getTotalPrice() {
    return this._items.reduce((total, item) => {
      const unit = this.toNum(item.price);
      const optionPrice = Object.values(item.selectedOptions || {}).reduce(
        (sum, opt) => sum + this.toNum(opt?.price ?? opt?.priceDelta),
        0,
      );
      const qty = this.toNum(item.count || 1);

      return total + (unit + optionPrice) * qty;
    }, 0);
  }

  getSelectedTotalPrice() {
    return this.selectedItems.reduce((total, item) => {
      const unit = this.toNum(item.price);
      const optionPrice = Object.values(item.selectedOptions || {}).reduce(
        (sum, opt) => sum + this.toNum(opt?.price ?? opt?.priceDelta),
        0,
      );
      const qty = this.toNum(item.count || 1);

      return total + (unit + optionPrice) * qty;
    }, 0);
  }

  get items() {
    return this._items;
  }

  get totalItems() {
    return this._items.reduce((sum, item) => sum + item.count, 0);
  }

  clearAll() {
    this.clearItems();
  }

  clearItems() {
    this._items = [];
    this.clearSelection();
    this.saveBasket();
  }

  saveBasket() {
    localStorage.setItem("basket", JSON.stringify(this._items));
  }

  loadBasket() {
    const savedItems = JSON.parse(localStorage.getItem("basket"));
    if (savedItems && Array.isArray(savedItems)) {
      this._items = savedItems;
    }
  }

  saveSelection() {
    localStorage.setItem("basket_selection", JSON.stringify(this._selected));
  }

  loadSelection() {
    try {
      const saved = JSON.parse(localStorage.getItem("basket_selection"));
      if (saved && typeof saved === "object") {
        this._selected = saved;
      }
    } catch {
      this._selected = {};
    }
  }
}

export default BasketStore;
